import React, { useCallback, useMemo, useState } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CategoryColor, EventModalProps, IEvent } from '../core/type';
import { BellRing, Briefcase, Calendar, Clock, Coffee, Gift, Heart, MapPin, Pencil, Tag, Trash2, Users, X } from 'lucide-react';
import DeleteConfirmationModal from '../../../apps/components/DeleteConfirmationModal';
import { deleteCalenderEvent } from '../core/request';
import { useSnackbar } from '../../../../../_theme/context/SnackbarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../redux/store';
import { deleteEventAction } from '../../../../../redux/actions/events.actions';
import { CustomDateFormate } from "../../../../../utils/date";
import { getEventCategoryColor, getEventEndDateTime, getEventStartDateTime, getFormattedDate, getFormattedTime, isMultiDayEvent } from '../utils';
import SetReminderPopup from './setReminderForm';
import { useAuth } from '../../../auth';
import { getApiErrorMessage } from "../../../../../utils/utils";

const EventModal: React.FC<EventModalProps> = ({
    show,
    onHide,
    date,
    events,
    setIsUpdateMode,
    setSelectedEvent,
    setShowAddEventModal,
    setEventsForModal,
    selectedEvent,
    isAllowActions = true
}) => {
    const { auth } = useAuth()
    const [showSetReminderModal, setShowSetReminderModal] = useState<boolean>(false);

    const { showSnackbar } = useSnackbar();
    const dispatch = useDispatch<AppDispatch>();
    const { categoryAndColorsData }: { categoryAndColorsData: CategoryColor[] } = useSelector(
        (state: RootState) => state.eventReducer
    );

    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Memoize formatted date
    const formattedDate = useMemo(() => {
        if (!date) return '';
        return CustomDateFormate(date);
    }, [date]);

    // Memoize category icons mapping
    const getCategoryIcon = useCallback((category: string) => {
        const icons: Record<string, React.ReactNode> = {
            'Work': <Briefcase size={14} />,
            'Personal': <Heart size={14} />,
            'Meeting': <Users size={14} />,
            'Health': <Heart size={14} />,
            'Social': <Coffee size={14} />,
            'Holiday': <Gift size={14} />
        };
        return icons[category] || <Tag size={14} />;
    }, []);

    // Optimized event display time calculation
    const getEventDisplayTime = useCallback((event: IEvent) => {
        const isMultiDay = isMultiDayEvent(event);

        if (getEventStartDateTime && getEventEndDateTime) {
            const startDateTime = getEventStartDateTime(event);
            const endDateTime = getEventEndDateTime(event);

            if (event.is_all_day) {
                if (isMultiDay) {
                    const startDate = getFormattedDate(startDateTime);
                    const endDate = getFormattedDate(endDateTime);
                    return `${startDate} - ${endDate} (All Day)`;
                }
                const date = getFormattedDate(startDateTime);
                return `${date} (All Day)`;
            }

            if (isMultiDay) {
                const startDate = getFormattedDate(startDateTime);
                const endDate = getFormattedDate(endDateTime);
                const startTime = getFormattedTime(startDateTime);
                const endTime = getFormattedTime(endDateTime);
                return `${startDate} ${startTime} - ${endDate} ${endTime}`;
            }

            const date = getFormattedDate(startDateTime);
            const startTime = getFormattedTime(startDateTime);
            const endTime = getFormattedTime(endDateTime);
            return `${date} • ${startTime} - ${endTime}`;
        }

        // Fallback for backward compatibility
        if (event.is_all_day) {
            if (isMultiDay) {
                return `${CustomDateFormate(event.start_date)} - ${CustomDateFormate(event.end_date)} (All Day)`;
            }
            return `${CustomDateFormate(event.start_date)} (All Day)`;
        }
        if (isMultiDay) {
            return `${CustomDateFormate(event.start_date)} ${event.start_time} - ${CustomDateFormate(event.end_date)} ${event.end_time}`;
        }
        return `${CustomDateFormate(event.start_date)} • ${event.start_time} - ${event.end_time}`;
    }, [getEventStartDateTime, getEventEndDateTime]);

    // Event handlers
    const handleUpdateEvent = useCallback((event: IEvent) => {
        setIsUpdateMode(true);
        setSelectedEvent(event);
        event?.user_id ? setShowAddEventModal(true) : setShowSetReminderModal(true);
        onHide();
    }, [setIsUpdateMode, setSelectedEvent, setShowAddEventModal]);

    const handleDeleteEvent = useCallback((event: IEvent) => {
        setSelectedEvent(event);
        setShowDeleteModal(true);
    }, [setSelectedEvent]);

    const handleConfirmDelete = useCallback(async () => {
        if (!selectedEvent) return;

        setLoading(true);
        try {
            const response = await deleteCalenderEvent({ event_id: selectedEvent.event_id });
            if (response.status === 'ok') {
                dispatch(deleteEventAction(selectedEvent.event_id));
                setEventsForModal(prevEvents => prevEvents.filter(event => event.event_id !== selectedEvent.event_id));
                onHide();
                showSnackbar(response.message, 'success');
            } else {
                showSnackbar(response.message, 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, 'Failed to delete event'), 'danger');
            console.error('Error deleting event:', error);
        } finally {
            setShowDeleteModal(false);
            setLoading(false);
        }
    }, [selectedEvent, dispatch, setEventsForModal, onHide, showSnackbar]);

    const handleHideReminderModal = () => {
        setShowSetReminderModal(false);
        setSelectedEvent(null);
    };

    // Memoize event card renderer for better performance
    const EventCard = useCallback(({ event }: { event: IEvent }) => {
        const displayTime = getEventDisplayTime(event);
        const eventLocation = event.location;
        const categoryColor = getEventCategoryColor(event.category, categoryAndColorsData);
        const categoryIcon = getCategoryIcon(event.category);
        const canSetReminder = auth && event.user_id == null;
        const isOwner =
            auth && Number(auth.db_user_id) === Number(event.user_id);

        return (
            <div key={event.id} className="event-card-modern bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
                <div className="p-3">
                    <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                            <div
                                className="category-icon rounded-3 d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: `${categoryColor}20`,
                                    color: categoryColor
                                }}
                            >
                                {categoryIcon}
                            </div>
                            <h5 className="fs-6 fw-bold m-0 text-gray-700">{event.title}</h5>
                        </div>
                        <div
                            className="px-2 py-1 rounded-pill fs-9 fw-semibold"
                            style={{
                                backgroundColor: `${categoryColor}20`,
                                color: categoryColor
                            }}
                        >
                            {event.category}
                        </div>
                    </div>

                    <div className="d-flex align-items-start gap-3 justify-content-between">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2 fs-9">
                                <Clock size={14} className="time-icon" />
                                <span className="time-text">{displayTime}</span>
                            </div>

                            {eventLocation && (
                                <div className="d-flex align-items-center gap-2 mb-2 fs-9">
                                    <MapPin size={14} />

                                    {event.mode === "online" ? (
                                        <a
                                            href={eventLocation}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary text-decoration-underline"
                                        >
                                            {/* {eventLocation} */}
                                            {new URL(eventLocation).hostname}
                                        </a>
                                    ) : (
                                        <span>{eventLocation}</span>
                                    )}
                                </div>
                            )}

                            {event.description && (
                                <div className="fs-9 text-gray-600 mt-2 pt-2 border-top">
                                    {event.description}
                                </div>
                            )}
                        </div>
                        <div className="flex-shrink-0 ms-3 d-flex gap-2">
                            {isOwner ? (
                                <>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Edit Event</Tooltip>}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleUpdateEvent(event)}
                                            className="btn btn-sm bg-light-success text-success d-flex align-items-center justify-content-center p-0"
                                            style={{
                                                width: "25px",
                                                height: "25px",
                                                borderRadius: "6px",
                                            }}
                                        >
                                            <Pencil size={16} />
                                        </button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Delete Event</Tooltip>}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteEvent(event)}
                                            className="btn btn-sm bg-light-danger text-danger d-flex align-items-center justify-content-center p-0"
                                            style={{
                                                width: "25px",
                                                height: "25px",
                                                borderRadius: "6px",
                                            }}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </OverlayTrigger>
                                </>
                            ) : canSetReminder ? (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Set Reminder</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        onClick={() => handleUpdateEvent(event)}
                                        className="btn btn-sm bg-light-primary text-primary d-flex align-items-center justify-content-center p-0"
                                        style={{
                                            width: "25px",
                                            height: "25px",
                                            borderRadius: "6px",
                                        }}
                                    >
                                        <BellRing size={16} />
                                    </button>
                                </OverlayTrigger>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [getEventDisplayTime, categoryAndColorsData, getCategoryIcon, handleUpdateEvent, handleDeleteEvent, auth]);

    // Memoize events list to prevent unnecessary re-renders
    const eventsList = useMemo(() => {
        if (events.length === 0) {
            return (
                <div className="text-center p-20">
                    <div>✨</div>
                    <h6>No Events Scheduled</h6>
                </div>
            );
        }

        return (
            <div className="position-relative" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="events-count-badge d-inline-block text-white fw-semibold fs-9 px-3 py-1 mb-4 rounded-pill">
                    {events.length} {events.length === 1 ? 'Event' : 'Events'}
                </div>
                {events.map(event => (
                    <EventCard key={event.id} event={event} />
                ))}
            </div>
        );
    }, [events, EventCard]);

    return (
        <Modal show={show} onHide={() => { onHide(), setShowSetReminderModal(false) }} centered size='lg'>
            <div className="position-relative bg-white rounded-4 overflow-auto shadow-sm m-5">
                {/* Decorative Header Gradient */}
                <div className="modal-gradient-bg"></div>

                <Modal.Header className="border-0 py-3 px-4 position-relative bg-white">
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center gap-2 bg-gray-200 py-2 px-4 rounded-pill fs-8 fw-semibold text-gray-700">
                            <Calendar size={18} />
                            <span>{formattedDate}</span>
                        </div>
                        <button
                            className="close-btn bg-white border-0 d-flex align-items-center justify-content-center rounded-circle cursor-pointer text-gray-700"
                            onClick={() => { onHide(), setShowSetReminderModal(false) }}
                        >
                            <X size={20} />
                        </button>
                    </div>
                </Modal.Header>

                <Modal.Body className="overflow-y-auto py-2 px-4">
                    {eventsList}


                    {showSetReminderModal && (<SetReminderPopup
                        onHide={handleHideReminderModal}
                        event={selectedEvent}
                        setSelectedEvent={setSelectedEvent}
                        setEventsForModal={setEventsForModal}
                        events={events}
                    />)}

                </Modal.Body>
            </div>

            <DeleteConfirmationModal
                show={showDeleteModal}
                popupLoader={loading}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                deleteMessage="Are you sure you want to delete this Event?"
            />
        </Modal>
    );
};

export default React.memo(EventModal);